'use client';

/**
 * Braze — client-side Web SDK loader.
 *
 * Dynamically imports `@braze/web-sdk` and initialises the Braze Web SDK
 * after the user grants **marketing (advertising) consent**. The SDK is
 * never fetched or executed before consent — the bundle (~80 KB gzipped)
 * stays off the critical path entirely until opt-in.
 *
 * **Consent posture (PECR / GDPR — see MWP-449)**
 *
 * Braze is a marketing platform. The most-compliant interpretation is
 * that the entire Braze SDK is gated on marketing consent, not analytics
 * consent. We do not load anonymous device profiles pre-consent.
 *
 * **What happens on opt-in**
 *
 *  1. Subscribe to `MARKETING_CONSENT_GRANTED_EVENT` and check existing
 *     consent on mount (for returning consented visitors).
 *  2. Dynamic-import `@braze/web-sdk` — the bundle only fetches now.
 *  3. `initialize()` with the workspace API key + EU `fra-02` endpoint.
 *  4. `openSession()` to start anonymous device tracking.
 *  5. Register the SDK instance via `setBrazeInstance` so the
 *     `brazeCapture` / `brazeIdentify` wrappers can use it.
 *
 * **What happens on opt-out**
 *
 *  `consent.ts` calls `brazeOptOut()` directly — `disableSDK()` +
 *  `wipeData()` + clearing the instance reference. This component does
 *  not need to handle withdrawal.
 *
 * Place once in the root layout.
 */

import { isBrazeLoaded, setBrazeInstance } from '@lib/analytics/braze';
import { hasMarketingConsent } from '@lib/analytics/consent';
import {
  BRAZE_API_KEY,
  BRAZE_LOADED_EVENT,
  BRAZE_SDK_ENDPOINT,
  IS_BRAZE_CONFIGURED,
  MARKETING_CONSENT_GRANTED_EVENT,
} from '@lib/analytics/constants';
import { useEffect } from 'react';

/**
 * Performs the actual Braze SDK boot sequence: post-import consent
 * re-check, `initialize()` with the PECR-compliant config, open a
 * session, register the instance, and notify listeners.
 *
 * Extracted out of `loadSdk` so the loader's cyclomatic complexity
 * stays under Fallow's CRAP threshold — the in-flight / already-loaded
 * guard plus this branch were enough to push it to the limit.
 *
 * Returns `true` if the SDK was activated, `false` if the call was
 * aborted by the consent re-check (e.g. user withdrew consent during
 * the dynamic import).
 */
const activateBrazeSdk = (
  braze: typeof import('@braze/web-sdk'),
  isCancelled: () => boolean,
): boolean => {
  // Re-validate consent state after the dynamic import resolves.
  // The import can take hundreds of ms on a cold cache; in that
  // window the user could open the cookie banner and withdraw
  // marketing consent. Without this guard we would still initialise
  // the SDK and open a tracking session despite withdrawal, because
  // the synchronous `brazeOptOut` triggered by `applyPreferences`
  // is a no-op when no SDK instance has been registered yet.
  if (isCancelled() || !hasMarketingConsent()) {
    return false;
  }

  braze.initialize(BRAZE_API_KEY, {
    baseUrl: BRAZE_SDK_ENDPOINT,
    enableLogging: false,
    // Use cookies + localStorage for device ID persistence. Allowed
    // because we only reach this code path after explicit marketing
    // consent — PECR-compliant.
    noCookies: false,
    // Defer in-app message handling until product integrates it.
    // Keeps the loader minimal and avoids unexpected UI.
    allowUserSuppliedJavascript: false,
    // Session timeout aligned with PostHog (30 minutes).
    sessionTimeoutInSeconds: 30 * 60,
  });

  braze.openSession();
  setBrazeInstance(braze);
  // Notify listeners (notably `BrazeIdentity`) that the SDK is
  // now usable. The async import means consumers can't rely on
  // `MARKETING_CONSENT_GRANTED_EVENT` alone — that fires
  // synchronously from `applyPreferences` long before the
  // dynamic import resolves.
  window.dispatchEvent(new Event(BRAZE_LOADED_EVENT));
  return true;
};

const Braze = () => {
  useEffect(() => {
    if (!IS_BRAZE_CONFIGURED) {
      return;
    }

    let cancelled = false;
    let loadInFlight = false;

    const loadSdk = async () => {
      // Guard against concurrent loads: the listener and the
      // already-consented mount path can both fire `loadSdk` (e.g.
      // returning visitor opens cookie modal and re-confirms). Without
      // this guard we would attempt to `initialize()` the SDK twice.
      if (loadInFlight || isBrazeLoaded()) {
        return;
      }
      loadInFlight = true;

      try {
        // Dynamic import — the `@braze/web-sdk` bundle is fetched only
        // after marketing consent is granted. Webpack code-splits it
        // into its own chunk.
        const braze = await import('@braze/web-sdk');
        activateBrazeSdk(braze, () => cancelled);
      } finally {
        loadInFlight = false;
      }
    };

    // Always listen for the marketing-consent-granted event — even if
    // the visitor has already consented on mount. If they later
    // withdraw consent (which calls `brazeOptOut` and clears the SDK
    // instance) and then re-grant consent during the same page
    // session, the listener is what re-runs `loadSdk` and re-mounts
    // the SDK. Returning early after the initial load would leave the
    // re-grant unhandled until a full page reload.
    //
    // New consent grants are handled immediately (user just made a
    // decision — no deferral). Returning visitors use requestIdleCallback
    // so the ~80 KB SDK dynamic import does not compete with hydration.
    const onConsent = () => {
      void loadSdk();
    };
    window.addEventListener(MARKETING_CONSENT_GRANTED_EVENT, onConsent);

    // For returning visitors who already have marketing consent, defer the
    // SDK load to an idle window. Braze is not needed until the first
    // marketing event (typically add_to_cart or identify), which requires
    // user interaction and cannot happen in the first few hundred ms anyway.
    // timeout: 5000 ensures init runs even on a heavily loaded mobile CPU.
    let ricId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;

    if (hasMarketingConsent()) {
      if ('requestIdleCallback' in window) {
        ricId = requestIdleCallback(() => void loadSdk(), { timeout: 5000 });
      } else {
        timerId = setTimeout(() => void loadSdk(), 0);
      }
    }

    return () => {
      cancelled = true;
      window.removeEventListener(MARKETING_CONSENT_GRANTED_EVENT, onConsent);
      if (ricId !== undefined) {
        cancelIdleCallback(ricId);
      }
      if (timerId !== undefined) {
        clearTimeout(timerId);
      }
    };
  }, []);

  return null;
};

export default Braze;
